import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

/**
 * PastePreviewDialog - Dialog for previewing clipboard data before importing
 * 
 * @param {boolean} isOpen - Controls dialog visibility
 * @param {function} onClose - Callback when dialog is closed
 * @param {Array} previewData - 2D array of data to preview
 * @param {string} targetTable - Target table name ('algae' or 'zooplankton')
 * @param {function} onImportClick - Callback when "Importer data" is clicked
 */
export default function PastePreviewDialog({
  isOpen,
  onClose,
  previewData,
  targetTable,
  onImportClick
}) {
  if (!isOpen || !previewData || previewData.length === 0) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            📋 Preview av data fra clipboard
          </DialogTitle>
          <p className="text-sm text-slate-600 mt-2">
            {targetTable === 'algae' ? 'Algeprøver' : 'Zooplankton'} - 
            {' '}{previewData.length} rader x {previewData[0]?.length || 0} kolonner
          </p>
        </DialogHeader>
        
        <div className="flex-1 overflow-auto border rounded-lg">
          <table className="w-full border-collapse text-sm">
            <thead className="bg-slate-100 sticky top-0">
              <tr>
                <th className="border border-slate-300 px-2 py-1 text-left font-semibold text-xs">Rad</th>
                {previewData[0]?.map((_, colIndex) => (
                  <th key={colIndex} className="border border-slate-300 px-2 py-1 text-center font-semibold text-xs">
                    Kol {colIndex + 1}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {previewData.map((row, rowIndex) => (
                <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                  <td className="border border-slate-300 px-2 py-1 font-semibold text-slate-600 text-xs">
                    {rowIndex + 1}
                  </td>
                  {row.map((cell, colIndex) => (
                    <td 
                      key={colIndex} 
                      className="border border-slate-300 px-2 py-1 text-center"
                    >
                      {cell === '<5000' ? (
                        <span className="text-slate-500 text-xs">&lt;5000</span>
                      ) : cell ? (
                        <span className="font-mono text-xs">{typeof cell === 'string' && !isNaN(cell) ? parseInt(cell).toLocaleString('nb-NO') : cell}</span>
                      ) : (
                        <span className="text-slate-300 text-xs">-</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Informasjon:</strong> Dataene vil bli fylt inn rad for rad, kolonne for kolonne i tabellen.
            Verdier som &quot;&lt;5000&quot; blir automatisk håndtert.
          </p>
        </div>
        
        <DialogFooter className="flex gap-2 pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => onClose(false)}
          >
            Avbryt
          </Button>
          <Button
            onClick={onImportClick}
            className="bg-gradient-to-r from-teal-500 to-blue-600 hover:from-teal-600 hover:to-blue-700"
          >
            <span className="text-lg mr-2">✓</span>
            Importer data
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
